import { useQuery } from '@tanstack/react-query'
import { Text, makeStyles, tokens, Spinner } from '@fluentui/react-components'
import { vacationBalanceService, VacationBalance } from '../services/vacationBalanceService'
import VacationTiles from './VacationTiles'

const useStyles = makeStyles({
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '16px',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '12px',
  },
})

function fmt(val: number) {
  return val % 1 === 0 ? String(val) : val.toFixed(1).replace('.', ',')
}

function BalanceDisplay({ balance, compact, styles }: {
  balance: VacationBalance
  compact?: boolean
  styles: ReturnType<typeof useStyles>
}) {
  // Zbývá = Celkem − Čerpáno − Plánováno (odečítá i čekající žádosti)
  const availHours = Math.round((balance.totalHours - balance.usedHours - balance.plannedHours) * 10) / 10
  const availDays  = balance.averageDailyHours > 0
    ? Math.round((availHours / balance.averageDailyHours) * 10) / 10 : 0

  const pct   = balance.totalHours > 0 ? Math.max(0, availHours / balance.totalHours) : 0
  const color = pct > 0.3 ? tokens.colorPaletteGreenForeground1
              : pct > 0.1 ? tokens.colorPaletteYellowForeground1
              : tokens.colorPaletteRedForeground1

  if (compact) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '16px',
        padding: '8px 12px',
        background: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        flexWrap: 'wrap',
      }}>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          Zbývá dovolená {balance.year}:
        </Text>
        <Text size={400} weight="semibold" style={{ color }}>
          {fmt(availHours)} h
        </Text>
        {!balance.workloadChangedInYear && (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            ({fmt(availDays)} dní)
          </Text>
        )}
        {balance.plannedHours > 0 && (
          <Text size={200} style={{ color: tokens.colorStatusWarningForeground1 }}>
            · naplánováno {fmt(balance.plannedHours)} h
          </Text>
        )}
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Text size={400} weight="semibold">Dovolená {balance.year}</Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground4 }}>
          úvazek {fmt(balance.weeklyHours)} h/týden · {fmt(balance.averageDailyHours)} h/den
        </Text>
      </div>
      <VacationTiles balance={balance} />
    </div>
  )
}

interface Props {
  employeeId?: number
  compact?: boolean
}

export default function VacationBalanceSummary({ employeeId, compact }: Props) {
  const styles = useStyles()

  const { data: balance, isLoading } = useQuery({
    queryKey: ['vacation-balance', employeeId ?? 'me'],
    queryFn: () => employeeId
      ? vacationBalanceService.getBalance(employeeId)
      : vacationBalanceService.getMyBalance(),
  })

  if (isLoading) return <Spinner size="tiny" />
  if (!balance) return null

  return <BalanceDisplay balance={balance} compact={compact} styles={styles} />
}
